import React from "react";
import { CreateEventRequest, EventType, EventVisibility, AccessLevel } from "@/types";
import { atLeastAccess } from "@/utils/accessLevel";
import { eventTypes } from "@/constants/eventTypes";
import { useTracks } from "@/hooks/useTracks";
import { formatDateTimeLocalInput } from "@/utils/dateFormat";
import { useTranslation } from "react-i18next";

interface EventFormProps {
  eventData: Partial<CreateEventRequest>;
  errors: Record<string, string>;
  onUpdateField: (
    field: keyof CreateEventRequest,
    value: string | number | boolean | undefined,
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  isEditing?: boolean;
  currentAccessLevel?: AccessLevel;
}

export const EventForm: React.FC<EventFormProps> = ({
  eventData,
  errors,
  onUpdateField,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isEditing = false,
  currentAccessLevel,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { data: tracks = [], isLoading: isLoadingTracks } = useTracks();

  // Snapshot the visibility the form opened with. Deriving the exemption from
  // the live value would unmount the grandfathered option the moment the user
  // selected anything else, with no way back short of a page reload.
  const initialVisibility = React.useRef<EventVisibility | undefined>(undefined);
  if (initialVisibility.current === undefined && eventData.visibility) {
    initialVisibility.current = eventData.visibility;
  }

  // Publicly discoverable visibilities need organizer access; a member editing
  // a grandfathered event keeps the value it already had.
  const canPublish = atLeastAccess(currentAccessLevel, "organizer");
  const offerInvitationOnly =
    canPublish || initialVisibility.current === "invitation_only";
  const offerPublic = canPublish || initialVisibility.current === "public";

  const formatDateTimeLocal = (dateString: string) => {
    if (!dateString) return "";
    return formatDateTimeLocalInput(new Date(dateString));
  };

  const handleDateTimeChange = (
    field: "startDate" | "endDate",
    value: string,
  ) => {
    if (value) {
      const isoString = new Date(value).toISOString();
      onUpdateField(field, isoString);
    } else {
      onUpdateField(field, "");
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.name")} *
        </label>
        <input
          type="text"
          id="name"
          value={eventData.name || ""}
          onChange={(e) => onUpdateField("name", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.name ? "border-red-500" : "border-secondary-300"
          }`}
          placeholder={t("events:form.namePlaceholder")}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="track"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.track")} *
        </label>
        <select
          id="track"
          value={eventData.trackId || ""}
          onChange={(e) => onUpdateField("trackId", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.trackId ? "border-red-500" : "border-secondary-300"
          }`}
          disabled={isLoadingTracks}
        >
          <option value="">
            {isLoadingTracks
              ? t("events:form.loadingTracks")
              : t("events:form.selectTrack")}
          </option>
          {tracks.length === 0 && !isLoadingTracks && (
            <option value="" disabled>
              {t("events:form.noTracksOption")}
            </option>
          )}
          {tracks.map((track) => {
            return (
              <option key={track.id} value={track.id}>
                {track.name}
              </option>
            );
          })}
        </select>
        {errors.trackId && (
          <p className="mt-1 text-sm text-red-600">{errors.trackId}</p>
        )}
        {tracks.length === 0 && !isLoadingTracks && (
          <p className="mt-1 text-sm text-secondary-500">
            {t("events:form.noTracksAvailable")}{" "}
            <a href="/tracks" className="text-primary-600 hover:underline">
              {t("events:form.noTracksCreateLink")}
            </a>{" "}
            {t("events:form.noTracksHelp")}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="eventType"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.eventType")} *
        </label>
        <select
          id="eventType"
          value={eventData.eventType || ""}
          onChange={(e) =>
            onUpdateField("eventType", e.target.value as EventType)
          }
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.eventType ? "border-red-500" : "border-secondary-300"
          }`}
        >
          <option value="">{t("events:form.selectEventType")}</option>
          {eventTypes.map((eventType) => (
            <option key={eventType.value} value={eventType.value}>
              {t(`events:types.${eventType.value}`)}
            </option>
          ))}
        </select>
        {errors.eventType && (
          <p className="mt-1 text-sm text-red-600">{errors.eventType}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("events:form.startDateTime")} *
          </label>
          <input
            type="datetime-local"
            id="startDate"
            value={formatDateTimeLocal(eventData.startDate || "")}
            onChange={(e) => handleDateTimeChange("startDate", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.startDate ? "border-red-500" : "border-secondary-300"
            }`}
          />
          {errors.startDate && (
            <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="endDate"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("events:form.endDateTime")} *
          </label>
          <input
            type="datetime-local"
            id="endDate"
            value={formatDateTimeLocal(eventData.endDate || "")}
            onChange={(e) => handleDateTimeChange("endDate", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.endDate ? "border-red-500" : "border-secondary-300"
            }`}
          />
          {errors.endDate && (
            <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("common:description")}
        </label>
        <textarea
          id="description"
          value={eventData.description || ""}
          onChange={(e) => onUpdateField("description", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={t("events:form.descriptionPlaceholder")}
        />
      </div>

      <div>
        <label
          htmlFor="descriptionUrl"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.eventUrl")}
        </label>
        <input
          type="url"
          id="descriptionUrl"
          value={eventData.descriptionUrl || ""}
          onChange={(e) => onUpdateField("descriptionUrl", e.target.value)}
          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={t("events:form.eventUrlPlaceholder")}
        />
        <p className="mt-1 text-sm text-secondary-500">
          {t("events:form.eventUrlHelp")}
        </p>
      </div>

      <div>
        <label
          htmlFor="applicationUrl"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.applicationUrl")}
        </label>
        <input
          type="url"
          id="applicationUrl"
          value={eventData.applicationUrl || ""}
          onChange={(e) => onUpdateField("applicationUrl", e.target.value)}
          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={t("events:form.applicationUrlPlaceholder")}
        />
        <p className="mt-1 text-sm text-secondary-500">
          {t("events:form.applicationUrlHelp")}
        </p>
      </div>

      <div>
        <label
          htmlFor="maxParticipants"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.maxParticipants")}
        </label>
        <input
          type="number"
          id="maxParticipants"
          value={eventData.maxParticipants || ""}
          onChange={(e) =>
            onUpdateField(
              "maxParticipants",
              e.target.value ? parseInt(e.target.value) : undefined,
            )
          }
          min="1"
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.maxParticipants ? "border-red-500" : "border-secondary-300"
          }`}
          placeholder={t("events:form.maxParticipantsPlaceholder")}
        />
        {errors.maxParticipants && (
          <p className="mt-1 text-sm text-red-600">{errors.maxParticipants}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="visibility"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("events:form.visibility")} *
        </label>
        <select
          id="visibility"
          value={eventData.visibility || "private"}
          onChange={(e) =>
            onUpdateField("visibility", e.target.value as EventVisibility)
          }
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.visibility ? "border-red-500" : "border-secondary-300"
          }`}
        >
          <option value="private">{t("events:form.visibilityPrivate")}</option>
          {offerInvitationOnly && (
            <option value="invitation_only">
              {t("events:form.visibilityInvitationOnly")}
            </option>
          )}
          {offerPublic && (
            <option value="public">
              {t("events:form.visibilityPublic")}
            </option>
          )}
        </select>
        {errors.visibility && (
          <p className="mt-1 text-sm text-red-600">{errors.visibility}</p>
        )}
        <p className="mt-1 text-sm text-secondary-500">
          {eventData.visibility === "private" &&
            t("events:form.visibilityPrivateHelp")}
          {eventData.visibility === "invitation_only" &&
            t("events:form.visibilityInvitationOnlyHelp")}
          {eventData.visibility === "public" &&
            t("events:form.visibilityPublicHelp")}
        </p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
          disabled={isSubmitting}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting
            ? t("common:saving")
            : isEditing
              ? t("events:form.update")
              : t("events:form.create")}
        </button>
      </div>
    </form>
  );
};
